import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useStudio } from '../../contexts/StudioContext';
import { vehicleSizes } from '../../data/schedule';
import { Booking, Draft } from '../../types/booking';
import { depositFor, priceFor, slotsForDate } from '../../utils/availability';
import { euro } from '../../utils/format';
import { StepService } from './StepService';
import { StepVehicle } from './StepVehicle';
import { StepWhen } from './StepWhen';
import { StepDetails } from './StepDetails';
import { Summary } from './Summary';
import { Confirmation } from './Confirmation';
const steps = [{
  id: 'service',
  tab: 'Behandeling',
  title: 'Wat heeft uw auto nodig?'
}, {
  id: 'vehicle',
  tab: 'Voertuig',
  title: 'Wat voor voertuig is het?'
}, {
  id: 'when',
  tab: 'Datum & tijd',
  title: 'Kies een moment'
}, {
  id: 'details',
  tab: 'Gegevens',
  title: 'Gegevens en betaling'
}];
const emptyDraft: Draft = {
  serviceId: null,
  extraIds: [],
  vehicleSizeId: null,
  date: null,
  time: null,
  paymentMethod: 'deposit',
  customer: {
    name: '',
    email: '',
    phone: '',
    car: '',
    plate: '',
    notes: ''
  }
};
export function BookingWizard({
  initialServiceId
}: {
  initialServiceId?: string | null;
}) {
  const {
    services,
    extras,
    settings,
    bookings,
    createBooking
  } = useStudio();
  const [draft, setDraft] = useState<Draft>(() => ({
    ...emptyDraft,
    serviceId: initialServiceId ?? null
  }));
  const [step, setStep] = useState(initialServiceId ? 1 : 0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [placed, setPlaced] = useState<Booking | null>(null);
  const [sending, setSending] = useState(false);
  const service = services.find(s => s.id === draft.serviceId) ?? null;
  const chosenExtras = extras.filter(e => draft.extraIds.includes(e.id));
  const size = vehicleSizes.find(v => v.id === draft.vehicleSizeId) ?? null;
  const price = useMemo(() => priceFor(service, chosenExtras, size), [service, chosenExtras, size]);
  const deposit = depositFor(price.amount, settings);
  const patch = (next: Partial<Draft>) => setDraft(prev => ({
    ...prev,
    ...next
  }));
  const ready = step === 0 ? Boolean(draft.serviceId) : step === 1 ? Boolean(draft.vehicleSizeId) : step === 2 ? Boolean(draft.date && draft.time) : true;
  const validate = () => {
    const next: Record<string, string> = {};
    const c = draft.customer;
    if (!c.name.trim()) next.name = 'Vul uw naam in.';
    if (!c.phone.trim()) next.phone = 'Wij bellen u voor de afspraak.';
    if (!c.email.trim()) next.email = 'Vul uw e-mailadres in.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) next.email = 'Dit e-mailadres lijkt niet te kloppen.';
    if (!c.car.trim()) next.car = 'Welk voertuig komt er langs?';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const goNext = () => {
    if (step < steps.length - 1) {
      setStep(s => s + 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    if (!validate()) return;
    const stillFree = slotsForDate(draft.date!, price.minutes, bookings, settings).some(s => s.time === draft.time && s.available);
    if (!stillFree) {
      setStep(2);
      patch({
        time: null
      });
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    setSending(true);
    window.setTimeout(() => {
      const booking = createBooking({
        serviceId: draft.serviceId!,
        extraIds: draft.extraIds,
        vehicleSizeId: draft.vehicleSizeId!,
        date: draft.date!,
        time: draft.time!,
        paymentMethod: draft.paymentMethod,
        customer: draft.customer
      });
      setSending(false);
      setPlaced(booking);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 900);
  };
  if (placed) {
    return <div className="mx-auto max-w-shell px-6 lg:px-10" data-id="element-323" data-mp="jr4fjtwqs9y9">
        <Confirmation booking={placed} onReset={() => {
        setDraft({
          ...emptyDraft
        });
        setStep(0);
        setErrors({});
        setPlaced(null);
      }} data-id="element-324" data-mp="jtqnvvrgithj" />
      </div>;
  }
  return <div className="mx-auto max-w-shell px-5 pb-32 pt-24 sm:px-6 lg:px-10 lg:pb-32 lg:pt-40" data-id="element-325" data-mp="21qlkkngk7fp2">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_330px] lg:gap-14" data-id="element-326" data-mp="1wpeeqtxz8ql1">
        <div data-id="element-327" data-mp="2bmwzzmbg4lnq">
          <ol className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm" data-id="element-328" data-mp="2bmf78znzspkk">
            {steps.map((s, i) => <li key={s.id} className="flex items-center gap-3" data-id="element-329" data-mp="26odd9jp135ub" data-mpi={i}>
                <button type="button" onClick={() => i < step && setStep(i)} disabled={i > step} className={`flex items-center gap-2 transition-colors duration-200 ease-smooth ${i === step ? 'text-chalk' : i < step ? 'text-dim hover:text-ember' : 'text-dim/40'}`} data-id="element-330" data-mp="2kga4zmmsbdq6">
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${i === step ? 'bg-ember font-semibold text-void' : i < step ? 'border border-ember/50 text-ember' : 'border border-edge text-dim/60'}`} data-id="element-331" data-mp="390n8z64g2crx">
                    {i + 1}
                  </span>
                  <span className="hidden sm:inline" data-id="element-332" data-mp="2v15dy90k23a4">{s.tab}</span>
                </button>
                {i < steps.length - 1 && <span className="h-px w-6 bg-edge sm:w-8" aria-hidden="true" data-id="element-333" data-mp="2gcrbw0ayqium" />}
              </li>)}
          </ol>

          <h1 className="mt-8 font-display text-3xl font-extrabold leading-[1.05] tracking-tightest text-chalk sm:text-4xl" data-id="element-334" data-mp="2lc3ycjxx9nwo">
            {steps[step].title}
          </h1>

          <div className="mt-9" data-id="element-335" data-mp="2bdo62dn5s8ll">
            <AnimatePresence mode="wait" initial={false} data-id="element-336" data-mp="2bcmum828dk3c">
              <motion.div key={steps[step].id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-337" data-mp="huxmxlk4ptjs">
                {step === 0 && <StepService draft={draft} onChange={patch} data-id="element-338" data-mp="36awoosyrhds" />}
                {step === 1 && <StepVehicle draft={draft} onChange={patch} data-id="element-339" data-mp="10t1jzhmqs258" />}
                {step === 2 && <StepWhen draft={draft} minutes={price.minutes} onChange={patch} data-id="element-340" data-mp="10r76godsvcb6" />}
                {step === 3 && <StepDetails draft={draft} errors={errors} deposit={deposit} amount={price.amount} onChange={patch} data-id="element-341" data-mp="qv3zn5806phw" />}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-12 hidden items-center justify-between gap-4 border-t border-edge pt-6 lg:flex" data-id="element-342" data-mp="37jhrlkh7w463">
            <button type="button" onClick={() => {
            setStep(s => Math.max(0, s - 1));
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }} disabled={step === 0} className="inline-flex items-center gap-2 text-sm text-dim transition-colors duration-200 ease-smooth hover:text-chalk disabled:opacity-40" data-id="element-343" data-mp="1n2qj92ylzwys">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-344" data-mp="25ronwurjnd7t" />
              Vorige
            </button>
            <button type="button" onClick={goNext} disabled={!ready || sending} className="group inline-flex items-center gap-2 rounded-full bg-ember px-7 py-4 text-sm font-semibold text-void transition-[background-color,box-shadow] duration-200 ease-smooth hover:bg-white hover:glow disabled:cursor-not-allowed disabled:opacity-40" data-id="element-345" data-mp="2apgijwjkpdfp">
              {sending ? 'Verwerken…' : step === steps.length - 1 ? draft.paymentMethod === 'deposit' ? `Betaal ${euro(deposit)} en bevestig` : 'Afspraak aanvragen' : 'Volgende stap'}
              {!sending && <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-smooth group-hover:translate-x-0.5" aria-hidden="true" data-id="element-346" data-mp="2w277z0lk46q1" />}
            </button>
          </div>
        </div>

        <Summary draft={draft} service={service} chosenExtras={chosenExtras} size={size} price={price} deposit={deposit} data-id="element-347" data-mp="24cm2saw2btz5" />
      </div>

      <div className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-edge bg-void/95 px-5 py-3 backdrop-blur-xl lg:hidden" data-id="element-348" data-mp="2daeel52ksjhs">
        <div className="flex items-center gap-3" data-id="element-349" data-mp="3or4rf71i89kd">
          {step > 0 && <button type="button" onClick={() => {
          setStep(s => Math.max(0, s - 1));
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }} aria-label="Vorige stap" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-edge text-chalk" data-id="element-350" data-mp="3osz4y0ag4zef">
              <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-351" data-mp="16hs1i9ui7c9q" />
            </button>}
          <span className="shrink-0" data-id="element-352" data-mp="1u7w2e9gb4xx1">
            <span className="block text-[11px] uppercase tracking-[0.14em] text-dim" data-id="element-353" data-mp="1y8fyrat23xi4">Totaal</span>
            <span className="font-display text-lg font-extrabold text-chalk" data-id="element-354" data-mp="1khj8hqmlrse8">{euro(price.amount)}</span>
          </span>
          <button type="button" onClick={goNext} disabled={!ready || sending} className="flex-1 rounded-full bg-ember px-5 py-3.5 text-sm font-semibold text-void transition-opacity duration-200 ease-smooth disabled:opacity-40" data-id="element-355" data-mp="1pfkwmpyvyhwk">
            {sending ? 'Verwerken…' : step === steps.length - 1 ? draft.paymentMethod === 'deposit' ? `Betaal ${euro(deposit)}` : 'Aanvragen' : 'Volgende'}
          </button>
        </div>
      </div>
    </div>;
}